import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { SmoothHouseIcon } from '@components/icons/smooth-house-icon';
import { kindTitles, productPath, type ProductSlug } from '@components/products/products';
import { FREEZE_FOR_CAPTURE, MotionReveal } from '@components/motion/scroll-motion';
import { ENGINE_KINDS, ENGINE_ORDER, type EngineKey } from '@components/wish-story/engines';
import { FooterCta, FooterCtaRow } from '@pages/landing-page.styles';
import { colors } from '@config/colors';
import { heightMedia, media } from '@config/breakpoints';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';
import { WISH_TODAY } from './download-panel';
import { FadingPhone } from './phone-fade';
import { storeCtaProps } from './store-cta';

/*
 * The wishes the app has been made better at, as wide cards that turn by
 * themselves (June, 2026-09-22: "just have them as cards … and have them
 * carousel through. No menu."): each the app answering it and what went
 * into making it better — the same three kinds How we build attaches, in
 * its order. Childcare is next: its card is there, coming soon. Each live
 * one leads to its own page (`@components/products`), which its table
 * reads from.
 *
 * June, 2026-09-23: the mom's wish and the phone carry each card — the
 * wish in the app's own coral bubble, the product's name a label over it —
 * with the three kinds as a compact list of pills in How we build's
 * colours; one link, "See More". The phones play the app's own screen
 * recordings at twice their speed, fading to white before the tab bar; the row turns on as each card's words are read, not waiting for
 * its recording, and only ever forward, round and round; and after
 * Childcare, a card to get the app that mirrors the page's Download panel.
 */

export type Demo = {
  key: string;
  /** The wish, as a mom puts it. */
  wish: string;
  title: string;
  soon?: boolean;
  /** Its own page, once it exists. */
  product?: ProductSlug;
  /** The app answering it: a screen recording at 2x over its own first
   *  frame (public/videos/demo, made from mediadb's
   *  `*_media_staging_demo_recording` originals; the product pages play
   *  the same recordings at 1x). */
  screen?: { video: string; poster: string; label: string };
  built: Record<EngineKey, string>;
};

/** Also the pre-seed deck's Demo slide (`pitch-deck/slides/demo-slide`). */
export const DEMOS: readonly Demo[] = [
  {
    key: 'adventures',
    wish: 'How do I get the kids out of the house?',
    title: 'Adventures',
    screen: {
      video: '/videos/demo/adventures-2x.mp4',
      poster: '/videos/demo/adventures.jpg',
      label: 'The app planning a Saturday: stops with drive times and opening hours, one swapped for a stop nearer by.',
    },
    product: 'adventures',
    built: kindTitles('adventures'),
  },
  {
    key: 'growth',
    wish: 'Are my kids growing okay?',
    title: 'Growth Curves',
    screen: {
      video: '/videos/demo/growth-curves-2x.mp4',
      poster: '/videos/demo/growth-curves.jpg',
      label: 'The app telling a child’s growth story: height today, predicted adult height, the family’s heights, and heights to aspire to.',
    },
    product: 'growth-curves',
    built: kindTitles('growth-curves'),
  },
  {
    key: 'commute',
    wish: 'New job… what’s the commute going to be?',
    title: 'Commute Optimizer',
    screen: {
      video: '/videos/demo/commute-2x.mp4',
      poster: '/videos/demo/commute.jpg',
      label: 'The app comparing a commute on foot, by bike, by transit and by car — time, cost, steps and calories — then all of them on one chart.',
    },
    product: 'commute-optimizer',
    built: kindTitles('commute-optimizer'),
  },
  {
    key: 'childcare',
    wish: 'Who can watch the kids on a sick day?',
    title: 'Childcare',
    soon: true,
    built: {
      data: 'School Calendars and Closures',
      expert: 'Chaos-Day and PTO Coverage Models',
      skills: 'Care Scheduled for Sick Days and Date Nights',
    },
  },
];

/** The cards in turn: the wishes, then where to get the app. */
type Item = { kind: 'demo'; demo: Demo } | { kind: 'app' };
const ITEMS: readonly Item[] = [...DEMOS.map((demo) => ({ kind: 'demo' as const, demo })), { kind: 'app' as const }];

/** The least a card holds before the carousel turns; the app card's all. */
const TURN_MS = 7500;

/* A wish's card holds as long as its words take to read (June, 2026-09-23:
   "auto advance through the demos at reading of the Demo text pace — don't
   wait for the demo to finish"): a moment to take it in, then a steady
   240 words a minute through its name, the wish and what made it better —
   about 10 to 12 seconds a card. Its recording plays on beneath for as long
   as the card holds. */
const TAKE_IN_MS = 2500;
const WORD_MS = 250;
const wordCount = (texts: readonly string[]) => texts.join(' ').split(/\s+/).filter(Boolean).length;
const readingMs = (demo: Demo) =>
  TAKE_IN_MS +
  WORD_MS *
    wordCount([demo.title, demo.wish, ...ENGINE_ORDER.flatMap((kind) => [ENGINE_KINDS[kind].label, demo.built[kind]])]);
const dwellMs = (item: Item) => (item.kind === 'demo' ? Math.max(TURN_MS, readingMs(item.demo)) : TURN_MS);

/* The row only ever turns forward (June, 2026-09-23): the last card is
   drawn again before the first, and the first two again after the last.
   Turning on from the last lands on the copy of the first, and the track
   steps back to the first itself, with nothing on screen changing. The
   copies are for the eye only: hidden from screen readers, out of the tab
   order, never playing. */
const RING = [ITEMS.length - 1, ...ITEMS.map((_, i) => i), 0, 1];
const FIRST = 1;
const isCopy = (slot: number) => slot < FIRST || slot >= FIRST + ITEMS.length;
/** The slot of the card itself that a copy stands in for. */
const own = (slot: number) => FIRST + RING[slot];

/* A short screen — a laptop, a tablet on its side, a small phone — can't
   give the heading two lines and the card its height: as in How we build,
   the heading takes one line (two on a phone). */
const SHORT = heightMedia.short;
const SHORT_PHONE = heightMedia.shortPhone;
/* A short phone's own layout: below the side-by-side card. */
const SMALL_PHONE = heightMedia.smallPhone;

type DemoSectionProps = {
  /** Called each time the carousel has turned through every card — the page's autoplay moves on then. */
  onCycleEnd?: () => void;
  /** The page is playing itself: a pointer resting on the cards doesn't hold them then. */
  pagePlaying?: boolean;
};

/** The slot nearest the track's middle. */
function centredCard(track: HTMLElement): number {
  const middle = track.scrollLeft + track.clientWidth / 2;
  let best = 0;
  let distance = Infinity;
  Array.from(track.children).forEach((child, i) => {
    const card = child as HTMLElement;
    const d = Math.abs(card.offsetLeft - track.offsetLeft + card.offsetWidth / 2 - middle);
    if (d < distance) {
      distance = d;
      best = i;
    }
  });
  return best;
}

/** Bring slot `i` to the track's middle, moving the track and not the page. */
function centreCard(track: HTMLElement, i: number, behavior: ScrollBehavior): void {
  const card = track.children[i] as HTMLElement | undefined;
  if (!card) return;
  const left = card.offsetLeft - track.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
  track.scrollTo({ left, behavior });
}

/** The carousel turns by itself while it's on screen — each card for its
 *  dwell, forward from the last to the first as from any card to the next
 *  — and says so after each whole turn. A pointer resting on it (while the
 *  page isn't playing itself) or focus inside it holds it; the reader's
 *  own swipe or keys stop it for good. Returns the slot in the middle, and
 *  whether the row is on screen. */
function useTurning(
  trackRef: React.RefObject<HTMLElement>,
  still: boolean,
  pagePlaying: boolean,
  onCycleEnd?: () => void,
): { slot: number; inView: boolean } {
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [slot, setSlot] = useState(FIRST);
  const onEnd = useRef(onCycleEnd);

  useEffect(() => {
    onEnd.current = onCycleEnd;
  }, [onCycleEnd]);

  // Open on the first card itself, not the copy before it.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (track) centreCard(track, FIRST, 'instant');
  }, [trackRef]);

  // Which slot the middle holds, once the track comes to rest.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let rest = 0;
    const onScroll = () => {
      window.clearTimeout(rest);
      rest = window.setTimeout(() => setSlot(centredCard(track)), 120);
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.clearTimeout(rest);
    };
  }, [trackRef]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || still) return undefined;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.4 });
    observer.observe(track);
    const enter = () => setHovered(true);
    const leave = () => setHovered(false);
    const focusIn = () => setFocused(true);
    const focusOut = (e: FocusEvent) => {
      if (!track.contains(e.relatedTarget as Node | null)) setFocused(false);
    };
    const stop = () => setStopped(true);
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop();
    };
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) stop();
    };
    track.addEventListener('mouseenter', enter);
    track.addEventListener('mouseleave', leave);
    track.addEventListener('focusin', focusIn);
    track.addEventListener('focusout', focusOut);
    track.addEventListener('touchstart', stop, { passive: true });
    track.addEventListener('wheel', onWheel, { passive: true });
    track.addEventListener('keydown', onKey);
    return () => {
      observer.disconnect();
      track.removeEventListener('mouseenter', enter);
      track.removeEventListener('mouseleave', leave);
      track.removeEventListener('focusin', focusIn);
      track.removeEventListener('focusout', focusOut);
      track.removeEventListener('touchstart', stop);
      track.removeEventListener('wheel', onWheel);
      track.removeEventListener('keydown', onKey);
    };
  }, [trackRef, still]);

  const held = focused || (hovered && !pagePlaying);
  useEffect(() => {
    const track = trackRef.current;
    if (!track || still || !inView || held || stopped) return undefined;
    // At rest on a copy: step to the card itself, which looks the same.
    if (isCopy(slot)) {
      centreCard(track, own(slot), 'instant');
      return undefined;
    }
    const id = window.setTimeout(() => {
      const next = slot + 1;
      // The leaving card holds its last frame as it goes, rather than
      // looping back to its first.
      track.querySelectorAll('video').forEach((video) => video.pause());
      // On from the last card, onto the first's copy: a whole turn.
      if (next === FIRST + ITEMS.length) onEnd.current?.();
      centreCard(track, next, 'smooth');
    }, dwellMs(ITEMS[RING[slot]]));
    return () => window.clearTimeout(id);
  }, [trackRef, still, inView, held, stopped, slot]);

  return { slot, inView };
}

/** A card's recording: still on its first frame until its card is in the
 *  middle and the row is on screen, then played from the top at 2x (baked
 *  in) until the card moves on, looping while the row is held. */
const Recording: React.FC<{ screen: NonNullable<Demo['screen']>; playing: boolean }> = ({ screen, playing }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (playing) {
      video.currentTime = 0;
      // A browser that won't play it leaves the first frame showing.
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [playing]);
  return (
    <video
      ref={ref}
      src={screen.video}
      poster={screen.poster}
      aria-label={screen.label}
      width={540}
      height={1174}
      muted
      playsInline
      loop
      preload="none"
    />
  );
};

/** Keep a copy out of the tab order and away from screen readers. */
const inertCopy = (el: HTMLElement | null) => el?.setAttribute('inert', '');

export const ProductDemos: React.FC<DemoSectionProps> = ({ onCycleEnd, pagePlaying = false }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  const trackRef = useRef<HTMLDivElement>(null);
  const { slot, inView } = useTurning(trackRef, still, pagePlaying, onCycleEnd);

  return (
    <Root>
      <Header>
        <MotionReveal>
          <Eyebrow>Demo</Eyebrow>
          <Heading>Wishes from Early Moms, Enhanced for You.</Heading>
        </MotionReveal>
      </Header>

      <Track ref={trackRef} role="region" aria-label="Demos" tabIndex={0}>
        {RING.map((index, at) => {
          const item = ITEMS[index];
          const copy = isCopy(at);
          const key = `${copy ? `copy-${at}` : 'card'}-${index}`;
          const shared = { ref: copy ? inertCopy : undefined, 'aria-hidden': copy ? true : undefined } as const;
          if (item.kind === 'app') {
            return (
              <AppCard key={key} {...shared}>
                <AppEyebrow>Get the App</AppEyebrow>
                <AppHeading>{WISH_TODAY.heading}</AppHeading>
                <AppSub>{WISH_TODAY.sub}</AppSub>
                <FooterCtaRow>
                  <CardCta as="a" {...storeCtaProps('ios', 'demos')}>
                    <AppleLogo weight="fill" /> App Store
                  </CardCta>
                  <CardCta as="a" {...storeCtaProps('android', 'demos')}>
                    <GooglePlayLogo weight="fill" /> Google Play
                  </CardCta>
                </FooterCtaRow>
              </AppCard>
            );
          }
          const { demo } = item;
          return (
            <Card key={key} {...shared}>
              <Name>
                {demo.title}
                {demo.soon && <SoonTag>Coming soon</SoonTag>}
              </Name>
              <Wish>“{demo.wish}”</Wish>
              <Built>
                <caption>What made {demo.title} better</caption>
                <tbody>
                  {ENGINE_ORDER.map((kind) => (
                    <tr key={kind}>
                      <th scope="row">
                        <Pill style={{ background: ENGINE_KINDS[kind].pill, color: ENGINE_KINDS[kind].pillInk }}>
                          {ENGINE_KINDS[kind].label}
                        </Pill>
                      </th>
                      <td>{demo.built[kind]}</td>
                    </tr>
                  ))}
                </tbody>
              </Built>
              {demo.product ? (
                <More to={productPath(demo.product)}>
                  See More<Hidden> about {demo.title}</Hidden> <span aria-hidden="true">→</span>
                </More>
              ) : null}
              <PhoneSpot>
                <Phone>
                  {demo.screen ? (
                    <Recording screen={demo.screen} playing={!still && !copy && inView && slot === at} />
                  ) : (
                    <Upcoming>
                      <UpcomingMark aria-hidden="true">
                        <SmoothHouseIcon />
                      </UpcomingMark>
                      <UpcomingTitle>{demo.title}</UpcomingTitle>
                      <UpcomingSoon>Coming soon</UpcomingSoon>
                      <UpcomingNote>Wish from the last mom</UpcomingNote>
                    </Upcoming>
                  )}
                </Phone>
              </PhoneSpot>
            </Card>
          );
        })}
      </Track>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  container-type: inline-size;
`;

const Header = styled.div`
  margin-bottom: ${spacing.lg};

  ${media.lg} {
    margin-bottom: ${spacing.xl};
  }

  ${SHORT} {
    margin-bottom: ${spacing.md};
  }
`;

const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${spacing.lg};

  ${SHORT} {
    margin-bottom: ${spacing.md};
  }
`;

const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.6vw, 3.75rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  max-width: 15em;
  margin: 0;
  text-wrap: balance;

  ${SHORT} {
    font-size: clamp(1.75rem, 2.6vw, 2.5rem);
    max-width: none;
  }
`;

/* The cards in a row that turns: each a wide card with its neighbours
   showing at the edges, centred as it turns in. The row runs out to the
   screen's edges on a phone, and fades at its ends. */
const EDGES = 'linear-gradient(to right, transparent 0, #000 3%, #000 97%, transparent 100%)';
const Track = styled.div`
  --card: 90cqw;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--card);
  column-gap: ${spacing.md};
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  margin: 0 -${spacing.lg};
  padding: ${spacing.xs} calc((100cqw - var(--card)) / 2 + ${spacing.lg}) ${spacing.lg};
  -webkit-mask-image: ${EDGES};
  mask-image: ${EDGES};

  &::-webkit-scrollbar {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid ${colors.highlight.dark};
    outline-offset: 4px;
  }

  ${media.sm} {
    --card: 84cqw;
    column-gap: ${spacing.lg};
  }

  ${media.lg} {
    --card: 80cqw;
    column-gap: ${spacing.xl};
  }
`;

const Soon = styled.span`
  font-family: ${typography.fontFamily.small};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: ${borderRadius.full};
  color: ${colors.white};
  background: linear-gradient(135deg, ${colors.accent.coral.exxtraDark}, ${colors.accent.coral.exxxtraDark});
`;

/* On a phone: the name and the wish across the top, then the whole screen
   beside what made it better. Wider: the words in a column, centred
   against a screen sized to the screen's height. */
const Card = styled.article`
  scroll-snap-align: center;
  display: grid;
  grid-template-columns: 92px 1fr;
  grid-template-areas:
    'name name'
    'wish wish'
    'phone table'
    'phone more';
  grid-template-rows: auto auto auto 1fr;
  column-gap: ${spacing.lg};
  row-gap: ${spacing.md};
  align-items: start;
  padding: ${spacing.lg};
  border-radius: ${borderRadius['3xl']};
  background: ${colors.overlayWhite70};
  border: 1px solid ${colors.overlayWhite90};
  box-shadow: 0 20px 50px ${colors.shadowSoft};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  ${media.sm} {
    grid-template-columns: 1fr auto;
    grid-template-rows: 1fr auto auto auto auto 1fr;
    grid-template-areas:
      '. phone'
      'name phone'
      'wish phone'
      'table phone'
      'more phone'
      '. phone';
    column-gap: ${spacing.xl};
    row-gap: ${spacing.md};
    align-items: center;
    padding: ${spacing.xl} ${spacing.xl} ${spacing.xl} ${spacing['2xl']};
  }

  ${media.lg} {
    column-gap: ${spacing['3xl']};
    padding: ${spacing['2xl']} ${spacing['2xl']} ${spacing['2xl']} ${spacing['3xl']};
  }

  ${SHORT} {
    padding: ${spacing.lg} ${spacing.xl} ${spacing.lg} ${spacing['2xl']};
  }

  ${SHORT_PHONE} {
    padding: ${spacing.md};
    row-gap: ${spacing.sm};
  }

  ${SMALL_PHONE} {
    grid-template-columns: 80px 1fr;
  }
`;

/* The product, as a label over the wish. */
const Name = styled.h3`
  grid-area: name;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm} ${spacing.md};
  font-family: ${typography.fontFamily.small};
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
  margin: 0;

  ${media.sm} {
    align-self: end;
  }
`;

/* The wish as a mom writes it in the app: its coral glass bubble, the
   card's headline. Ink on the glass holds 4.5:1 (see colors.chat). */
const Wish = styled.p`
  grid-area: wish;
  justify-self: start;
  max-width: 18em;
  margin: 0;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: 22px 22px 6px 22px;
  border: 1px solid ${colors.chat.glassRim};
  background: linear-gradient(180deg, ${colors.chat.userGlassTop}, ${colors.chat.userGlassBottom});
  box-shadow: 0 10px 24px ${colors.shadowSoft};
  color: ${colors.neutral.extraDark};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1.1875rem, 2.5vw, 1.875rem);
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: -0.01em;
  /* Wrapped as a chat bubble wraps — balanced lines would leave the
     bubble's width empty at their ends — without a lone last word. */
  text-wrap: pretty;

  ${media.sm} {
    padding: ${spacing.md} ${spacing.xl} ${spacing.md} ${spacing.lg};
  }

  ${SHORT_PHONE} {
    font-size: 1.0625rem;
    padding: ${spacing.sm} ${spacing.md};
  }
`;

const SoonTag = styled(Soon)`
  font-size: 10px;
  letter-spacing: 0.12em;
`;

/* What went into the answer: the three kinds, in How we build's order and
   colours — each a pill, then what it is. Stacked while the column is
   narrow; side by side three across. */
const Built = styled.table`
  grid-area: table;
  width: 100%;
  border-collapse: collapse;

  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  tr {
    display: block;
  }

  tr + tr {
    margin-top: ${spacing.sm};
  }

  th,
  td {
    display: block;
    padding: 0;
    text-align: left;
  }

  td {
    margin-top: 3px;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 0.875rem;
    line-height: 1.35;
    color: ${colors.neutral.darkMedium};
  }

  ${media.lg} {
    tr {
      display: table-row;
    }

    th,
    td {
      display: table-cell;
      vertical-align: middle;
      padding: 5px 0;
    }

    th {
      width: 1%;
      white-space: nowrap;
      padding-right: ${spacing.lg};
    }

    td {
      margin: 0;
      font-size: 0.9375rem;
    }
  }
`;

/* A kind, as a pill in its colour: ink on the pink and the purple, white
   on the magenta (see ENGINE_KINDS). */
const Pill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: ${borderRadius.full};
  font-family: ${typography.fontFamily.small};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.6;
  text-transform: uppercase;
  white-space: nowrap;

  ${media.md} {
    padding: 3px 10px;
    font-size: 11px;
  }
`;

/* Where a live card leads: its own page. */
const More = styled(Link)`
  grid-area: more;
  justify-self: start;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${colors.brand.exxxtraDark};

  &:hover {
    text-decoration: underline;
  }
`;

const Hidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

/* Where the phone stands, its foot fading away: the phone's own size, so
   the fade falls across its lower quarter. */
const PhoneSpot = styled(FadingPhone)`
  grid-area: phone;
  align-self: start;
`;

/* The whole screen in a slate bezel, sized to the screen's height so the
   whole section fits it (0.46 is the capture's width over its height) —
   as large as that allows. On a phone, beside what made it better. */
const Phone = styled.div`
  width: 100%;
  aspect-ratio: 900 / 1955;
  border-radius: 16px;
  padding: 4px;
  background: ${colors.neutral.extraDark};
  overflow: hidden;

  img,
  video {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
  }

  ${media.sm} {
    border-radius: 28px;
    padding: 6px;
    width: clamp(140px, calc((100vh - 400px) * 0.46), 250px);
    width: clamp(140px, calc((100svh - 400px) * 0.46), 250px);

    img,
    video {
      border-radius: 22px;
    }
  }
`;

/* Childcare's screen, before there is one. */
const Upcoming = styled.div`
  height: 100%;
  min-height: 100%;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};
  padding: ${spacing.lg};
  text-align: center;
  background: radial-gradient(circle at 50% 38%, ${colors.brand.extraLight} 0%, ${colors.brand.primary} 60%, ${colors.brand.primaryDark} 100%);
`;

const UpcomingMark = styled.span`
  width: 40px;
  height: 40px;
  color: ${colors.highlight.dark};

  ${media.sm} {
    width: 64px;
    height: 64px;
  }
`;

const UpcomingTitle = styled.span`
  display: none;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 1.125rem;
  font-weight: 500;
  color: ${colors.neutral.extraDark};

  ${media.sm} {
    display: block;
  }
`;

const UpcomingSoon = styled(Soon)`
  display: none;

  ${media.sm} {
    display: inline;
  }
`;

const UpcomingNote = styled.span`
  display: none;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${colors.neutral.darkMedium};
  max-width: 12em;

  ${media.sm} {
    display: block;
  }
`;

/* The last card: where to get the app — the page's Download panel again,
   in the row: its eyebrow, "Wish Today", its line and the hero's coral
   buttons, centred. */
const AppCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
  text-align: center;

  ${media.sm} {
    display: flex;
  }
`;

const AppEyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

const AppHeading = styled.h3`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2rem, 4.6vw, 3.25rem);
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  margin: 0;
`;

const AppSub = styled.p`
  max-width: 26em;
  margin: 0 0 ${spacing.sm};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  font-weight: 300;
  line-height: 1.5;
  color: ${colors.highlight.primary};
`;

/* The hero's buttons, a size down where a phone's card is narrow. */
const CardCta = styled(FooterCta)`
  justify-content: center;
  padding: ${spacing.md} ${spacing.lg};
  font-size: 0.875rem;

  ${media.sm} {
    padding: ${spacing.lg} ${spacing['2xl']};
    font-size: ${typography.fontSize.base};
  }
`;
